/**
 * Transitions - Animation durations and easing functions.
 *
 * Usage:
 *   import { transitions } from '@/theme';
 *   sx={{ transition: `opacity ${transitions.duration.fast} ${transitions.easing.standard}` }}
 *   // Or use shorthand:
 *   sx={{ transition: transitions.fade }}
 *
 * Organization:
 * - duration: How long animations take
 * - easing: Acceleration curves
 * - Shorthands: Common transition combinations
 *
 * Adding new values:
 * - Extract from existing code, don't invent
 * - Durations in ms (150, 200, 300)
 * - Prefer standard easings over custom bezier curves
 */

/** Animation durations */
export const duration = {
  /** 0.15s - Fast feedback (opacity fades, quick hovers) */
  fast: '0.15s',
  /** 0.2s - Standard transitions (most UI changes) */
  standard: '0.2s',
  /** 250ms - Button transitions */
  button: '250ms',
  /** 0.3s - Slower transitions (width changes, sidebars) */
  slow: '0.3s',
  /** 200ms - Card stack slide transitions */
  cardSlide: '200ms',
} as const;

/** Easing functions */
export const easing = {
  /** Standard easing for most transitions */
  standard: 'ease',
  /** Ease-in-out for symmetrical animations */
  smooth: 'ease-in-out',
  /** Spring-like deceleration for card slides */
  decelerate: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
} as const;

/** Common transition shorthands */
export const shorthands = {
  /** Opacity fade - fast */
  fade: `opacity ${duration.fast}`,
  /** Background color change */
  bgColor: `background-color ${duration.standard} ${easing.standard}`,
  /** Box shadow change (hover effects) */
  shadow: `box-shadow ${duration.standard}`,
  /** Border color change (selection states) */
  border: `border-color ${duration.standard}`,
  /** Transform (movement effects) */
  transform: `transform ${duration.standard}`,
  /** Width change (collapsible elements) */
  width: `width ${duration.slow} ${easing.standard}`,
  /** All properties - standard */
  all: `all ${duration.standard}`,
  /** All properties - with ease */
  allEased: `all ${duration.standard} ${easing.standard}`,
  /** Button transitions */
  button: `all ${duration.button}`,
} as const;

/** Combined export */
export const transitions = {
  duration,
  easing,
  ...shorthands,
} as const;
